import type { ForumSessionUser } from "@ricetext/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AppContextValue } from "../app-context";
import { isApiClientError } from "../lib/api/client";
import {
  getForumSession,
  loginForumSession,
  logoutForumSession,
} from "../lib/api/session";
import { identities } from "../lib/seed";
import type { SeedIdentity } from "../lib/types";

const identityStorageKey = "ricetext:identity";
const anonymousIdentity: SeedIdentity = {
  id: "anonymous",
  name: "未登录",
  role: "reader",
  avatar: "访",
  coins: 0,
  replied: false,
};

export function isDemoAuthEnabled(): boolean {
  const configured = import.meta.env.VITE_DEMO_AUTH;
  return configured === undefined ? import.meta.env.DEV : configured === "true";
}

function getStoredIdentity(): SeedIdentity {
  const stored = localStorage.getItem(identityStorageKey);
  return identities.find((item) => item.id === stored) ?? identities[0]!;
}

export function mapSessionIdentity(user: ForumSessionUser): SeedIdentity {
  return {
    id: user.id,
    name: user.name,
    role: user.role,
    avatar: user.avatar,
    coins: user.coins,
    replied: user.replied,
  };
}

/** 开发环境使用可切换的本地身份，生产环境使用 HttpOnly 服务端会话。 */
export function useForumIdentity(): AppContextValue {
  const demo = isDemoAuthEnabled();
  const [identity, setIdentityState] = useState<SeedIdentity>(() =>
    demo ? getStoredIdentity() : anonymousIdentity,
  );
  const [authStatus, setAuthStatus] = useState<AppContextValue["authStatus"]>(
    demo ? "authenticated" : "loading",
  );

  const refreshIdentity = useCallback(async () => {
    if (demo) return;
    try {
      const session = await getForumSession();
      setIdentityState(mapSessionIdentity(session.current));
      setAuthStatus("authenticated");
    } catch (error) {
      setIdentityState(anonymousIdentity);
      setAuthStatus(isApiClientError(error) && error.status === 401 ? "unauthenticated" : "error");
    }
  }, [demo]);

  useEffect(() => {
    void refreshIdentity();
  }, [refreshIdentity]);

  const logout = useCallback(async () => {
    if (demo) return;
    try {
      await logoutForumSession();
    } finally {
      setIdentityState(anonymousIdentity);
      setAuthStatus("unauthenticated");
    }
  }, [demo]);

  return useMemo(
    () => ({
      identity,
      authMode: demo ? "demo" : "session",
      authStatus,
      setIdentity(next: SeedIdentity) {
        if (!demo) return;
        setIdentityState(next);
        localStorage.setItem(identityStorageKey, next.id);
      },
      async login(username: string, password: string) {
        if (demo) return;
        await loginForumSession(username, password);
        await refreshIdentity();
      },
      logout,
      refreshIdentity,
    }),
    [authStatus, demo, identity, logout, refreshIdentity],
  );
}
